import React, { useEffect, useRef, useState, useCallback, memo } from "react";

import { Link } from "react-router-dom";
import { useSidebar } from "@stores/SidebarContext";
import { ThemeToggleButton } from "./ThemeToggleButton";
import UserDropdown from "./UserDropdown";
import ReportsModalStore from "@stores/ReportsModalStore";
import DownloadModalStore from "@stores/DownloadModalStore";
import InfoModalStore from "@stores/InfoModalStore";
import { Download, Menu, Ellipsis } from "lucide-react";

import clsx from "clsx";

const AppHeader = memo(() => {
  const [isApplicationMenuOpen, setApplicationMenuOpen] = useState(false);

  const { isMobileOpen, toggleSidebar, toggleMobileSidebar } = useSidebar();

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (window.innerWidth >= 991) {
        toggleSidebar();
      } else {
        toggleMobileSidebar();
      }
    },
    [toggleSidebar, toggleMobileSidebar]
  );

  const toggleApplicationMenu = useCallback(() => {
    setApplicationMenuOpen((prev) => !prev);
  }, []);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const renderDownloadCenterIcon = () => {
    return (
      <div className="text-white text-center w-max flex">
        <button
          type="button"
          className={clsx(
            "cursor-pointer grayscale hover:grayscale-0",
            DownloadModalStore.showing && "grayscale-0"
          )}
          onClick={() => {
            ReportsModalStore.close();
            InfoModalStore.setShowing(false);
            DownloadModalStore.setShowing(!DownloadModalStore.showing);
          }}
          title="Download Center"
        >
          <Download className="size-6 dark:stroke-white stroke-gray-700 hover:!stroke-primary-300 transition-all duration-300" />
        </button>
      </div>
    );
  };

  return (
    <header className="sticky z-[12] top-0 flex w-full bg-white border-gray-200 z-99999 dark:border-gray-800 dark:bg-gray-900 lg:border-b">
      <div className="flex flex-col items-center justify-between grow lg:flex-row lg:px-6">
        <div className="flex items-center justify-between w-full gap-2 px-3 py-3 border-b border-gray-200 dark:border-gray-800 sm:gap-4 lg:justify-normal lg:border-b-0 lg:px-0 lg:py-4">
          <button
            type="button"
            className="items-center justify-center w-10 h-10 text-gray-500 border-gray-200 rounded-lg z-99999 dark:border-gray-800 lg:flex dark:text-gray-400 lg:h-11 lg:w-11 lg:border"
            onClick={handleToggle}
            aria-label="Toggle Sidebar"
          >
            <Menu />
          </button>

          <Link to="/" className="lg:hidden">
            <img
              className="dark:hidden"
              src="./images/logo/SwittleLogo.png"
              alt="Logo"
              width={158}
              height={30}
            />
            <img
              className="hidden dark:block"
              src="./images/logo/SwittleLogo.png"
              alt="Logo"
              width={130}
            />
          </Link>

          <button
            onClick={toggleApplicationMenu}
            className="flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg z-99999 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 lg:hidden"
          >
            <Ellipsis />
          </button>
        </div>
        <div
          className={`${
            isApplicationMenuOpen ? "flex" : "hidden"
          } items-center justify-between w-full gap-4 px-5 py-4 lg:flex shadow-theme-md lg:justify-end lg:px-0 lg:shadow-none`}
        >
          <div className="flex items-center gap-2 2xsm:gap-3"></div>
          <ThemeToggleButton />
          {renderDownloadCenterIcon()}
          <UserDropdown />
        </div>
      </div>
    </header>
  );
});

AppHeader.displayName = "AppHeader";

export default AppHeader;
